/* istanbul ignore file */
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { Cookie } from '@common-vue/util/src/modules/cookie';
import { brand } from 'config';
import useDiscovery from '@modules/discovery/composables/useDiscovery';
import pageInformationUtil from '@utils/pageInformationUtil';
import generateUrlPathUtil from '@utils/generateUrlPathUtil';
import { useSortableGrid } from '@modules/sortable-grid/composables';
import xapi from '@services/xapi';

/**
 * Composable for retrieving facets page data.
 *
 * @description
 * This composable is used to fetch facets
 *
 * @returns {Object} An object containing the `loadFacets` function for fetching facets data
 *                   and the `facets` computed property to access facets.
 */

let facets;
let discovery;
let productCount;
let totalFacetSelectedCount;
let facetsMeta;
let router;
let route;
let filteringFacetsData;

async function loadFacets(data) {
  await discovery.loadPage(data);
}

function updateFacetsUrl(query) {
  /**
   * Triggers route push only if route changed to
   * avoid scroll restoration issues
   */
  /* istanbul ignore else */
  if (route.fullPath !== query) router.push(query);
}

function sortFacets(_facets) {
  return _facets.sort((a, b) => {
    /* istanbul ignore else */
    if (a < b) {
      return -1;
    }
    /* istanbul ignore else */
    if (a > b) {
      return 1;
    }
    return 0;
  });
}

const prioritizedFacets = brand === 'mcom' ? ['Delivery & Pickup', 'Delivery'] : ['Delivery', 'Pickup & Delivery'];
const parseFilters = (selectedFacets) => {
  const filters = selectedFacets;
  const order = Object.keys(selectedFacets);
  return { filters, order };
};

const updateMultiSelectRange = (facet, metaObj, transformedFacet) => {
  /* istanbul ignore else */
  if (facet.facetType === 'MULTISELECTRANGE') {
    const selected = metaObj?.selected?.find((selectFacet) => selectFacet?.name === facet?.name);
    const firstVal = selected?.values[0];
    const isCustomValue = facet?.values?.find((val) => val?.value === firstVal?.value) === undefined;
    /* istanbul ignore else */
    if (firstVal && isCustomValue) {
      transformedFacet?.values?.push({
        label: `Custom: ${firstVal?.displayName}`,
        displayName: `Custom: ${firstVal?.value}`,
        value: firstVal?.value,
        selected: true,
      });
      /* istanbul ignore else */
      if (!transformedFacet?.selectedValues?.length) transformedFacet?.selectedValues.push(firstVal.value);
    }
  }
};

const getSelectedDnfValues = (filters) => {
  const selectedValues = [];
  /* istanbul ignore else */
  if (filters.value.filters.UPC_SDD_PURCHASABLE) {
    selectedValues.push(`SDD=${filters.value.filters.UPC_SDD_PURCHASABLE}`);
  }
  /* istanbul ignore else */
  if (filters.value.filters.UPC_BOPS_PURCHASABLE) {
    selectedValues.push(`BOPS=${filters.value.filters.UPC_BOPS_PURCHASABLE}`);
  }
  return selectedValues;
};

const getSelectedSizeFacet = (facet) => {
  const sizeSelected = [];
  if (facet.children) {
    facet.children?.forEach((item) => {
      let selectedObj;
      if (item.children) {
        item.children.forEach((child) => {
          /* istanbul ignore else */
          if (child.selectedCount) {
            selectedObj = {
              name: child.name,
              selectedValues: child?.values?.reduce((acc, val) => {
                /* istanbul ignore else */
                if (val.selected || val.personal) {
                  acc.push(val.originalVal || val.value);
                }
                return acc;
              }, []),
            };
            sizeSelected.push(selectedObj);
          }
          const title = child?.values.find((val) => val.title);
          /* istanbul ignore else */
          if (!title) {
            child?.values.unshift({ title: child.displayName });
          }
          child?.values.forEach((val) => {
            /* istanbul ignore else */
            if (!val.originalVal && !val.title) {
              val.originalVal = val.value;
              val.value = `${val.value}_${child.name}`;
              val.dataName = child.name;
            }
          });
        });
      } else if (item.selectedCount) {
        selectedObj = {
          name: item.name,
          selectedValues: item?.values?.reduce((acc, val) => {
            /* istanbul ignore else */
            if (val.selected || val.personal) {
              acc.push(val.value);
            }
            return acc;
          }, []),
        };
        sizeSelected.push(selectedObj);
      }
    });
  } else {
    facet.values?.forEach((val) => {
      /* istanbul ignore else */
      if (val.selected) {
        sizeSelected.push(val.value);
      }
    });
  }
  return sizeSelected;
};

const getSelectedValues = (facet, filters) => {
  let selectedFacets = [];
  if (facet.facetType === 'MULTISELECTBUTTON') {
    selectedFacets = getSelectedSizeFacet(facet);
  } else if (facet.facetType === 'DELIVERY_AND_PICKUP') {
    selectedFacets = getSelectedDnfValues(filters);
  } else {
    selectedFacets = filters.value.filters[facet.name] || [];
  }
  return selectedFacets;
};

const transformFacets = (facet, filters) => {
  let selectionOrder = -1;
  if (facet.children) {
    facet.children.forEach(({ name }) => {
      const order = filters.value.order.indexOf(name.toUpperCase());
      /* istanbul ignore else */
      if (order >= 0) {
        selectionOrder = order;
      }
    });
  } else {
    selectionOrder = filters.value.order.indexOf(facet.name.toUpperCase());
  }

  const transformedFacet = {
    name: facet.displayName,
    facetType: facet.facetType,
    displayName: facet.name,
    selectedValues: getSelectedValues(facet, filters),
    values: facet.values ? facet.values.map((val) => ({
      ariaLabel: val.value,
      label: val.displayName,
      value: val.value,
      count: val.count,
      url: val.url || '',
    })) : [],
    children: facet.children ? facet.children : [],
    alwaysDropdown: facet.alwaysDropdown,
    selectionOrder: (selectionOrder >= 0) ? selectionOrder : 10000,
  };
  updateMultiSelectRange(facet, facetsMeta.value, transformedFacet);
  return transformedFacet;
};

const getSelectedSizes = (meta) => (meta?.selected ? meta.selected.filter((item) => item.name === 'SIZE_TYPE') : []);

export default function useFacets(pageInformation = pageInformationUtil()) {
  const store = useStore();
  const slideoutFacets = ref(null);
  router = useRouter();
  route = useRoute();

  const mySizesData = ref({});

  const disableDeliveryAndFulfillmentFacet = computed(() => store?.getters?.getDisableDeliveryAndFulfillmentFacet);

  const isDeliveryAndPickUpAvailable = computed(() => store.getters.getIsDomestic && !disableDeliveryAndFulfillmentFacet?.value);

  const { selectedFacets: xapiSelectedFacets } = useSortableGrid();

  /* get selectedFacets from the xapi response */
  const filters = computed(() => parseFilters(xapiSelectedFacets.value));

  const addDeliveryAndPickup = (facetsArray, alwaysDropdown = true) => {
    const dnfDislayName = 'Delivery & Pickup';
    let dnpArray = [];
    /* istanbul ignore else */
    if (isDeliveryAndPickUpAvailable.value) {
      dnpArray = [{
        name: 'DELIVERY_AND_PICKUP',
        label: 'DELIVERY_AND_PICKUP',
        selectedValues: [],
        displayName: brand === 'mcom' ? dnfDislayName : 'Pickup & Delivery',
        values: [
          {
            url: '/Upc_sdd_purchasable',
            value: `SDD=${filters.value.filters.UPC_SDD_PURCHASABLE || ''}`,
          },
          {
            url: '/Upc-bops-purchasable',
            value: `BOPS=${filters.value.filters.UPC_BOPS_PURCHASABLE || ''}`,
          },
        ],
        facetType: 'DELIVERY_AND_PICKUP',
        collapsed: true,
        indexable: true,
        alwaysDropdown,
      }];
    }

    return [...dnpArray, ...facetsArray];
  };
  /* istanbul ignore else */
  if (!facets) {
    facets = computed(() => {
      const facetsWithBOPS = [...store.getters.getFacets];
      const facetsNoBOPS = facetsWithBOPS.filter((val) => val.name !== 'UPC_BOPS_PURCHASABLE');
      const facetsWithDNF = addDeliveryAndPickup(facetsNoBOPS, false);
      return facetsWithDNF;
    });
    productCount = computed(() => store.getters.getProductCount);
    facetsMeta = computed(() => store.getters.getFacetsMeta);
    totalFacetSelectedCount = computed(() => store.getters.getSelectedFacetCount);
    filteringFacetsData = computed(() => facets.value.map((facet) => transformFacets(facet, filters)));
    discovery = useDiscovery();
    const mySizePreferences = !!facetsMeta.value?.facetPreferences?.sizePreferences;
    /* istanbul ignore else */
    if (mySizePreferences) {
      mySizesData.value.enabled = mySizePreferences;
      mySizesData.value.toggleAppearance = mySizePreferences;
      mySizesData.value.toggleValue = mySizePreferences;
      mySizesData.value.selectedSizes = getSelectedSizes(facetsMeta.value);
    }
  }

  const getSelectedFacetPath = (selectedFacets) => {
    const selectedNames = [];
    const selectedValuesList = []; // the order should be the same as for selectedNames
    const selectedValues = {};

    // TODO refactor too complicated
    [...selectedFacets].forEach((facet) => {
      const facetName = facet.name;
      if (facet.facetType === 'MULTISELECTBUTTON' && facet?.children.length) {
        facet.selectedValues.forEach((value) => {
          const fName = value.name.toLowerCase();
          selectedNames.push(fName[0].toUpperCase() + fName.slice(1));
          let facetValue = `${selectedValues[facetName] ? `${selectedValues[facetName]},` : ''}${encodeURIComponent(value.selectedValues.join('|'))}`.split(',');
          facetValue = facetValue.map((val) => val.replace(/%2C/gi, encodeURIComponent('%2C'))); // , should be encoded twice
          selectedValues[facetName] = facetValue;
        });
      } else if (facet.facetType === 'DELIVERY_AND_PICKUP') {
        facet.selectedValues.forEach((val) => {
          if (val.includes('SDD=')) {
            selectedNames.push('Upc_sdd_purchasable');
            selectedValuesList.push(val?.substring(4));
          } else if (val.includes('BOPS=')) {
            selectedNames.push('Upc_bops_purchasable');
            selectedValuesList.push(val?.substring(5));
          }
        });
      } else {
        const fName = facet.displayName.toLowerCase();
        selectedNames.push(fName[0].toUpperCase() + fName.slice(1));
        selectedValues[facetName] = facet.selectedValues.map((val) => {
          const encoded = encodeURIComponent(val?.replace(/\|/g, '%7C'));
          const noSpecialSymbols = encoded?.replace(/'/g, '%27');
          return noSpecialSymbols;
        }).join('|');
      }
      /* istanbul ignore else */
      if (facet.facetType !== 'DELIVERY_AND_PICKUP') {
        selectedValuesList.push(selectedValues[facetName]);
      }
    });

    const flattedSelectedValuesList = [].concat(...selectedValuesList);
    // TODO remove once the above is refactored
    const sortedSelectedNames = sortFacets([...selectedNames]);
    const sortedSelectedValuesList = sortedSelectedNames.map((name) => {
      const index = selectedNames.indexOf(name);
      return flattedSelectedValuesList[index];
    });

    const facetsUrl = sortedSelectedNames?.length ? `/${sortedSelectedNames}/${sortedSelectedValuesList}` : '';
    const facetsParams = { facetsUrl, selectedNames: sortedSelectedNames, selectedValuesList: sortedSelectedValuesList };
    const fullPath = computed(() => route.fullPath);
    const isPagination = false;
    const paginationData = [...store.getters.getPathUrls];
    const selectedFacetUrl = store.getters.getSelectedFacetUrl;
    const metaBaseUrl = store.getters.getFacetMetaBaseUrl;

    const pathname = generateUrlPathUtil({
      fullPath: fullPath.value, paginationData, facetsParams, isPagination, selectedFacetUrl, metaBaseUrl,
    });
    paginationData?.push({ key: 'Pageindex', value: 1 });
    store.dispatch('SET_PATHURLS', paginationData);
    return pathname;
  };

  const trasformFacetsData = (facetsData, metaObj, { selectedFacets }) => {
    const facetsMap = facetsData.map((facet) => {
      const selectedFacet = selectedFacets.find((f) => f.name === facet.displayName);
      const selectionOrder = (selectedFacet !== undefined && selectedFacet.selectionOrder !== undefined) ? selectedFacet.selectionOrder : 10000;
      const selectedValues = facet.facetType === 'MULTISELECTBUTTON'
        ? getSelectedSizeFacet(facet)
        : [...(selectedFacet?.selectedValues || [])];

      // transformed store facet data, which contains facet url
      const singleFacetData = filteringFacetsData.value.find((filteringFacet) => filteringFacet.displayName === facet.name) || {};
      const transformedFacet = {
        name: facet.displayName,
        facetType: facet.facetType,
        displayName: facet.name,
        selectedValues,
        values: facet.values ? facet.values.map((val, index) => ({
          ariaLabel: val.value,
          label: val.displayName,
          value: val.value,
          count: val.count,
          url: singleFacetData?.values?.[index]?.url || '',
        })) : [],
        children: facet.children ? facet.children : [],
        selectionOrder,
      };
      updateMultiSelectRange(facet, metaObj, transformedFacet);
      return transformedFacet;
    });
    return facetsMap;
  };

  const clearSelectedVisualFilters = (currentVFValue) => {
    /* istanbul ignore else */
    if (typeof window === 'undefined') return;
    const vfContainer = document.querySelector('.visual-filter-cont');
    /* istanbul ignore else */
    if (!vfContainer) return;
    const selectedVFs = vfContainer.querySelectorAll('.category-icon.selected');
    selectedVFs.forEach((element) => {
      /* istanbul ignore else */
      if (!currentVFValue || !decodeURIComponent(element?.href)?.includes(currentVFValue)) {
        element.classList.remove('selected');
      }
    });
  };

  const applyVisualFilters = ({
    vfsMatchFacets, lastAppliedVisialFilter, nextSelectedFacets, visualFilters, selectedFacets,
  }) => {
    let appliedFilters = [];
    /* istanbul ignore else */
    if (!vfsMatchFacets && lastAppliedVisialFilter && !lastAppliedVisialFilter?.initiallyLoaded) {
      clearSelectedVisualFilters(lastAppliedVisialFilter?.selectedValues[0]);
      appliedFilters.push(lastAppliedVisialFilter);
    } else if (nextSelectedFacets.length) {
      let visualFiltersInSelectedFacets = false;
      nextSelectedFacets.forEach((facet) => {
        visualFilters.forEach((visualFilter) => {
          /* istanbul ignore else */
          if (visualFilter.deletedFacets && facet.displayName === visualFilter.displayName) {
            facet.selectedValues = facet.selectedValues.filter((value) => !visualFilter.deletedFacets.includes(value));
            visualFiltersInSelectedFacets = true;
          } else if (facet.displayName === visualFilter.displayName) {
            visualFilter.selectedValues.forEach((selectedVF) => {
              /* istanbul ignore else */
              if (!facet.selectedValues.includes(selectedVF)) {
                facet.selectedValues.push(selectedVF);
              }
            });
            visualFiltersInSelectedFacets = true;
          }
        });
      });

      const nonEmptynextSelectedFacets = nextSelectedFacets.filter((facet) => facet.selectedValues.length);
      const currentVisualFilters = store.getters['visualFilterStore/getSelectedVisualFilters'] || [];
      /* istanbul ignore else */
      if (!visualFiltersInSelectedFacets && visualFilters.length === 1) {
        nonEmptynextSelectedFacets.push(visualFilters[0]);
      } else if (!visualFiltersInSelectedFacets && currentVisualFilters[0]?.selectedValues?.length) {
        nonEmptynextSelectedFacets.push(...currentVisualFilters);
      }
      if (visualFilters.length > nextSelectedFacets.length) {
        const selectedFacetsCategories = nonEmptynextSelectedFacets.map((f) => f.displayName);
        visualFilters.forEach((vf) => {
          /* istanbul ignore else */
          if (!selectedFacetsCategories.includes(vf.displayName) && vf.selectedValues.length) {
            nonEmptynextSelectedFacets.push(vf);
          }
        });
      }
      appliedFilters.push(...nonEmptynextSelectedFacets);
    } else if (visualFilters.length === 1 && !visualFilters[0].selectedValues.length) {
      store.commit('visualFilterStore/setSelectedFacets', []);
      /* istanbul ignore else */
      if (typeof isSelectedFacetsSorty !== 'undefined') {
        appliedFilters = [
          { name: 'Sortby', displayName: 'Sortby', selectedValues: [selectedFacets?.allSelected?.sortBy] },
        ];
      }
    } else if (visualFilters.length === 0) {
      const currentVisualFilters = store.getters['visualFilterStore/getSelectedVisualFilters'] || [];
      /* istanbul ignore else */
      if (currentVisualFilters[0]?.selectedValues?.length) {
        appliedFilters.push(...currentVisualFilters);
      }
    } else {
      const nonEmptyVisualFilters = visualFilters.filter((facet) => facet.selectedValues.length);
      appliedFilters.push(...nonEmptyVisualFilters);
    }

    return appliedFilters;
  };

  const getSelectedFacets = ((selectedFacets, visualFilters, areVisualsSelected) => {
    const filtersAndSortingData = [];
    const isSelectedFacetsSorty = selectedFacets?.allSelected?.sortBy;
    /* istanbul ignore else */
    if (typeof isSelectedFacetsSorty !== 'undefined') {
      filtersAndSortingData.push({ name: 'Sortby', displayName: 'Sortby', selectedValues: [selectedFacets?.allSelected?.sortBy] });
    }
    const nextSelectedFacets = selectedFacets.length === 0 ? [] : [...selectedFacets?.allSelected?.facets];

    const allVisialFilters = store.getters['visualFilterStore/getAllVisialFilters'];
    let vfsMatchFacets = true;
    let lastAppliedVisialFilter = null;
    /* istanbul ignore else */
    if (allVisialFilters?.slots) {
      lastAppliedVisialFilter = store?.getters['visualFilterStore/getLastAppliedVisialFilter'];
      const allFacets = store?.getters?.getFacets;
      const vfCategoryIndex = allFacets?.findIndex((f) => f?.name?.toLowerCase() === lastAppliedVisialFilter?.displayName?.toLowerCase());
      if (vfCategoryIndex === -1) {
        vfsMatchFacets = false;
      } else {
        const getFacetValues = (facetsList, index) => facetsList[index]?.values?.map((f) => f?.displayName);
        vfsMatchFacets = lastAppliedVisialFilter?.selectedValues?.every((vfValue) => getFacetValues(allFacets, vfCategoryIndex)?.includes(vfValue));
      }
    }
    /* istanbul ignore else */
    if (areVisualsSelected) {
      const appliedFilters = applyVisualFilters({
        vfsMatchFacets, lastAppliedVisialFilter, nextSelectedFacets, visualFilters, selectedFacets,
      });
      filtersAndSortingData.push(...appliedFilters);
    } else if (!areVisualsSelected && !selectedFacets?.allSelected?.facets.length) {
      /* istanbul ignore else */
      if (Object.values(filters?.value?.filters)?.length && slideoutFacets?.value?.totalSelectedFacets !== '') {
        /* istanbul ignore else */
        if (lastAppliedVisialFilter && !vfsMatchFacets) {
          filtersAndSortingData.push(lastAppliedVisialFilter);
          const facetPath = getSelectedFacetPath(filtersAndSortingData);
          updateFacetsUrl(facetPath);
          return;
        }
      }
      store.commit('visualFilterStore/setSelectedFacets', []);
      /* istanbul ignore else */
      if (allVisialFilters?.slots) {
        store.commit('visualFilterStore/removeLastAppliedVisualFilter');
        clearSelectedVisualFilters();
      }
      filtersAndSortingData.push(...nextSelectedFacets);
    } else {
      const allFacetsCategories = store?.getters.getFacets?.map((f) => f.name.toLowerCase());
      const vfsCategoryMatchFacets = allFacetsCategories?.includes(lastAppliedVisialFilter?.displayName.toLowerCase());
      /* istanbul ignore else */
      if (lastAppliedVisialFilter) {
        const vfInNextSelectedFacets = nextSelectedFacets.map((f) => f?.displayName).includes(lastAppliedVisialFilter?.displayName);
        /* istanbul ignore else */
        if (vfInNextSelectedFacets) {
          nextSelectedFacets.forEach((selectedFacet) => {
            /* istanbul ignore else */
            if (selectedFacet?.displayName === lastAppliedVisialFilter?.displayName) {
              lastAppliedVisialFilter.selectedValues = lastAppliedVisialFilter.selectedValues.filter((v) => selectedFacet.selectedValues.includes(v));
            }
          });
          store.commit('visualFilterStore/setSelectedFacets', [lastAppliedVisialFilter]);
        } else if (vfsCategoryMatchFacets) {
          store.commit('visualFilterStore/setSelectedFacets', []);
        }
      } else if (allVisialFilters?.slots) {
        const visualFiltersCategoies = allVisialFilters.slots?.map((f) => {
          const urlParts = f?.imageMap?.children[0]?.url?.split('?')[0]?.split('/') || [];
          return urlParts[urlParts.length - 2]?.toLowerCase();
        });
        store.commit('visualFilterStore/setSelectedFacets', nextSelectedFacets.filter((f) => visualFiltersCategoies.includes(f.displayName.toLowerCase())));
      }

      const notMatchedVF = !nextSelectedFacets.some((sf) => sf?.displayName === lastAppliedVisialFilter?.displayName);
      /* istanbul ignore else */
      if (notMatchedVF && lastAppliedVisialFilter?.selectedValues?.length && !vfsCategoryMatchFacets) {
        filtersAndSortingData.push(lastAppliedVisialFilter);
      }
      filtersAndSortingData.push(...nextSelectedFacets);
    }
    const facetPath = getSelectedFacetPath(filtersAndSortingData);
    updateFacetsUrl(facetPath);
  });

  const selectedOptions = (async (selectedFacets) => {
    const paginationData = {};
    paginationData.perPage = store?.getters?.getCurrentProductsperpage;
    paginationData.pageIndex = store?.getters?.getCurrentPageindex;
    paginationData.sortBy = store?.getters?.getCurrentSortby;
    const _customerExperiment = store?.getters?.getMeta?.context?.customerExperiment;

    // GWP query parameters (fallback to store so embed/filter requests always have category id)
    const { productId: queryProductId, CategoryID: categoryIdFromQuery, id: categoryIdFromId, _ffSearchQuery } = route.query;
    const CategoryID = categoryIdFromQuery ?? categoryIdFromId ?? store.getters.getAnalyticsData?.categoryID;

    const info = {
      queryProductId,
      _customerExperiment,
      ...pageInformation.value,
      facetsOnly: true,
      _ffSearchQuery,
      ...paginationData,
      CategoryID,
      id: CategoryID,
      categoryId: CategoryID,
    };

    const lastAppliedVisialFilter = store?.getters['visualFilterStore/getLastAppliedVisialFilter'];
    /* istanbul ignore else */
    if (lastAppliedVisialFilter && !selectedFacets.isClearAll) {
      const allFacetsCategories = store?.getters?.getFacets?.map((f) => f?.name?.toLowerCase());
      const vfInFacets = allFacetsCategories?.includes(lastAppliedVisialFilter?.displayName?.toLowerCase());
      /* istanbul ignore else */
      if (!vfInFacets) {
        selectedFacets?.selectedFacets?.push(lastAppliedVisialFilter);
      }
    }
    const facetPath = getSelectedFacetPath(selectedFacets.selectedFacets, false).split('?')[0];
    const response = await xapi.facetsXapiRequest(facetPath, info);
    const facetList = response?.body?.canvas?.rows ? response.body.canvas.rows
      .find((item) => !!item.rowSortableGrid)
      ?.rowSortableGrid : undefined;
    const metaObj = facetList?.zones[0]?.facets?.meta;
    let updatedFacets = facetList?.zones[0]?.facets?.facets || [];
    updatedFacets = updatedFacets.filter((val) => val.name !== 'UPC_BOPS_PURCHASABLE');
    updatedFacets = addDeliveryAndPickup(updatedFacets, false);
    const totalProductCount = metaObj?.productCount || '';
    const totalSelectedFacets = Object.values(metaObj?.selectedFacets || {}).flatMap((values) => values).length || '';
    const transformedData = trasformFacetsData(updatedFacets, metaObj, selectedFacets);
    slideoutFacets.value = { facets: transformedData, productCount: totalProductCount, totalSelectedFacets };
  });

  const saveMySizesToggle = (async (payload, checked) => {
    mySizesData.value.toggleValue = !!checked;
    const headers = {
      'X-Macys-Userguid': Cookie.get('bloomingdales_online_guid'),
    };
    await xapi.saveMySizesToggleXapi('DESKTOP', payload, headers);
  });

  return {
    loadFacets,
    facets,
    productCount,
    filteringFacetsData,
    prioritizedFacets,
    updateFacetsUrl,
    getSelectedFacets,
    selectedOptions,
    getSelectedSizeFacet,
    parseFilters,
    slideoutFacets,
    totalFacetSelectedCount,
    trasformFacetsData,
    mySizesData,
    saveMySizesToggle,
    disableDeliveryAndFulfillmentFacet,
    isDeliveryAndPickUpAvailable,
  };
}
